import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { authDownload } from "@/lib/authDownload";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, Trash2, User, Pencil, Send } from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader, EmptyState, Field, Input, Textarea, Select } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";
import { caseLabel, clientLabel } from "@/lib/labels";
import FilterPill from "@/components/FilterPill";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";
import StorageMeter from "@/components/StorageMeter";

export default function Documents() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [cases, setCases] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [aiBusyId, setAiBusyId] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ display_name: "", client_id: "", case_id: "" });
    const [edit, setEdit] = useState(null);            // { id, display_name, client_id, case_id, reason }
    const [editSaving, setEditSaving] = useState(false);
    const [esign, setEsign] = useState(null);          // e-sign send modal state
    const [esignSending, setEsignSending] = useState(false);
    const fileRef = useRef(null);
    const [params] = useSearchParams();
    const statusFilter = params.get("status");        // pending | pending_sig

    const visibleItems = useMemo(() => {
        if (!statusFilter) return items;
        if (statusFilter === "pending_sig") return items.filter(d => ["pending", "sent"].includes(d.signature_status));
        if (statusFilter === "pending")     return items.filter(d => ["pending", "draft"].includes(d.status));
        return items;
    }, [items, statusFilter]);
    const filterLabel = statusFilter === "pending_sig" ? "Pending signature only" : statusFilter === "pending" ? "Pending documents only" : null;
    const { getRowProps } = useRowKeyboardNav({ items: visibleItems, onOpen: (d) => openEdit(d), disabled: !!edit || open || !!esign });

    const load = async () => {
        try { const { data } = await api.get("/documents"); setItems(data); }
        catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => {
        load();
        api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
        api.get("/cases").then(r => setCases(r.data || [])).catch(() => {});
    }, []);

    // Cases visible in picker — narrow by client when one is chosen (two-way sync below handles auto-fill)
    const filteredCases = useMemo(() => {
        if (!form.client_id) return cases;
        return cases.filter(c => c.client_id === form.client_id);
    }, [cases, form.client_id]);

    // 2-way sync: picking a Client clears case if it doesn't belong; picking a Case auto-fills its Client.
    const onPickClient = (cid) => {
        const caseOk = !form.case_id || cases.find(c => c.id === form.case_id)?.client_id === cid;
        setForm(f => ({ ...f, client_id: cid, case_id: caseOk ? f.case_id : "" }));
    };
    const onPickCase = (cid) => {
        const c = cases.find(x => x.id === cid);
        setForm(f => ({ ...f, case_id: cid, client_id: c?.client_id || f.client_id }));
    };

    const openEsign = (d) => {
        // Default to client + counsel if those are tied to the document
        const client = clients.find(c => c.id === d.client_id);
        setEsign({
            document_id: d.id,
            document_name: d.display_name || d.original_filename,
            provider: "docusign",
            subject: `Please sign — ${d.display_name || d.original_filename}`,
            blurb: "",
            recipients: client?.email
                ? [{ name: client.name || "Client", email: client.email, routing_order: 1, role: "Client" }]
                : [{ name: "", email: "", routing_order: 1, role: "Client" }],
        });
    };

    const addRecipient = () => setEsign(s => ({
        ...s,
        recipients: [...s.recipients, { name: "", email: "", routing_order: s.recipients.length + 1, role: "Signer" }],
    }));
    const updRecipient = (i, k, v) => setEsign(s => {
        const next = [...s.recipients];
        next[i] = { ...next[i], [k]: v };
        return { ...s, recipients: next };
    });
    const rmRecipient = (i) => setEsign(s => ({ ...s, recipients: s.recipients.filter((_, idx) => idx !== i) }));

    const sendEsign = async (e) => {
        e.preventDefault();
        const valid = esign.recipients.every(r => r.name.trim() && r.email.trim());
        if (!valid) { toast.error("Every recipient needs a name and email"); return; }
        setEsignSending(true);
        try {
            const { data } = await api.post(`/esign/envelopes/send/${esign.document_id}`, {
                email_subject: esign.subject,
                email_blurb: esign.blurb,
                provider: esign.provider,
                recipients: esign.recipients.map(r => ({
                    name: r.name.trim(), email: r.email.trim(),
                    routing_order: parseInt(r.routing_order) || 1,
                    role: r.role || "Signer",
                })),
            });
            toast.success(`${esign.provider === "docusign" ? "DocuSign envelope" : "DigiLocker request"} sent (${data.envelope_id?.slice(0, 8)}…)`);
            setEsign(null);
        } catch (err) { toast.error(apiError(err)); }
        finally { setEsignSending(false); }
    };

    const openEdit = (d) => setEdit({
        id: d.id,
        display_name: d.display_name || "",
        client_id: d.client_id || "",
        case_id: d.case_id || "",
        reason: "",
        _orig: d,
    });

    const editFilteredCases = useMemo(() => {
        if (!edit?.client_id) return cases;
        return cases.filter(c => c.client_id === edit.client_id);
    }, [cases, edit?.client_id]);

    const onEditPickClient = (cid) => {
        const caseOk = !edit.case_id || cases.find(c => c.id === edit.case_id)?.client_id === cid;
        setEdit(e => ({ ...e, client_id: cid, case_id: caseOk ? e.case_id : "" }));
    };
    const onEditPickCase = (cid) => {
        const c = cases.find(x => x.id === cid);
        setEdit(e => ({ ...e, case_id: cid, client_id: c?.client_id || e.client_id }));
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit.reason || edit.reason.trim().length < 3) {
            toast.error("Remarks are mandatory (minimum 3 characters)"); return;
        }
        const diff = {};
        ["display_name", "client_id", "case_id"].forEach(k => {
            if ((edit[k] ?? "") !== (edit._orig[k] ?? "")) diff[k] = edit[k] || null;
        });
        if (Object.keys(diff).length === 0) { toast.error("No fields changed"); return; }
        setEditSaving(true);
        try {
            await api.patch(`/documents/${edit.id}`, diff, { headers: { "X-Change-Reason": edit.reason.trim() } });
            toast.success("Document re-mapped · logged to audit trail");
            setEdit(null);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
    };

    const onUpload = async (e) => {
        e.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Choose a file"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        if (form.display_name) fd.append("display_name", form.display_name);
        if (form.client_id) fd.append("client_id", form.client_id);
        if (form.case_id) fd.append("case_id", form.case_id);
        fd.append("doc_type", "general");
        try {
            await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setOpen(false);
            setForm({ display_name: "", client_id: "", case_id: "" });
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (e) { toast.error(apiError(e)); } finally {
            setUploading(false);
        }
    };

    const summarize = async (d) => {
        setAiBusyId(d.id);
        try {
            // Summarise from metadata for non-text files (fast path); text files get content read by AI.
            const text = `Document name: ${d.original_filename}\nType: ${d.content_type}\nSize: ${d.size} bytes\nUploaded at: ${d.created_at}.`;
            const { data } = await api.post("/ai/summary", { text, kind: "document" });
            // store summary client-side
            setItems(it => it.map(x => x.id === d.id ? { ...x, ai_summary: data.summary } : x));
            toast.success("AI summary ready");
        } catch (e) { toast.error(apiError(e)); } finally { setAiBusyId(null); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        try { await api.delete(`/documents/${id}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Documents"
                subtitle="Petitions, agreements, evidence — encrypted, versioned, retrievable."
                action={(
                    <GoldButton onClick={() => setOpen(true)} disabled={uploading} data-testid="documents-upload-btn">
                        <Upload className="w-4 h-4 inline mr-2" strokeWidth={2} /> {uploading ? "Uploading…" : "Upload"}
                    </GoldButton>
                )}
            />

            <div className="mb-5"><StorageMeter/></div>

            {filterLabel && (
                <div className="mb-4 flex items-center gap-2" data-testid="documents-filter-banner">
                    <FilterPill label={filterLabel} testId={`documents-pill-${statusFilter}`} tone="warning"/>
                    <span className="text-xs text-muted-foreground">{visibleItems.length} document{visibleItems.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {visibleItems.length === 0 ? (
                <EmptyState
                    title={filterLabel ? "No matching documents" : "No documents"}
                    subtitle={filterLabel ? "Try clearing the filter to see all documents." : "Upload your first petition, agreement or judgment."}
                    action={<GoldButton onClick={() => setOpen(true)} data-testid="documents-empty-upload">Upload document</GoldButton>}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                    {visibleItems.map((d, idx) => (
                        <div key={d.id} {...getRowProps(idx)} className="bg-muted p-6 flex flex-col hover:bg-muted/70 transition-colors" data-testid={`document-card-${d.id}`}>
                            <div className="flex items-start justify-between mb-4">
                                <FileText className="w-6 h-6 text-primary" strokeWidth={1.5}/>
                                <div className="flex items-center gap-2">
                                    {d.uploaded_by_client && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-2 py-0.5">
                                            <User className="w-3 h-3" strokeWidth={1.5}/> Client
                                        </span>
                                    )}
                                    <button onClick={() => openEdit(d)} data-testid={`document-edit-${d.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                                    <button onClick={() => remove(d.id)} data-testid={`document-delete-${d.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                                </div>
                            </div>
                            <button type="button" onClick={() => openEdit(d)}
                                    data-testid={`document-open-${d.id}`}
                                    className="text-left cursor-pointer flex-1 flex flex-col">
                                <div className="font-serif text-lg mb-1 truncate">{d.display_name || d.original_filename}</div>
                                {d.display_name && d.display_name !== d.original_filename && (
                                    <div className="text-xs text-muted-foreground mb-2 truncate font-mono">{d.original_filename}</div>
                                )}
                                <div className="text-xs text-muted-foreground mb-3 font-mono">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                                {(d.case_id || d.client_id) && (
                                    <div className="text-[10px] text-primary mb-3 font-mono uppercase tracking-[0.2em]" data-testid={`document-mapping-${d.id}`}>
                                        {(() => {
                                            const c = cases.find(x => x.id === d.case_id);
                                            const cl = clients.find(x => x.id === d.client_id);
                                            return [c?.internal_number, c?.court_case_number, cl?.code].filter(Boolean).join(" · ");
                                        })()}
                                    </div>
                                )}
                                <div className="text-xs text-muted-foreground mb-4">{fmtDateTime(d.created_at)}</div>
                                {d.ai_summary ? (
                                    <div className="bg-background border border-primary/30 p-3 text-xs text-muted-foreground mb-4 leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap">{d.ai_summary}</div>
                                ) : null}
                            </button>
                            <div className="flex gap-2 mt-auto">
                                <button onClick={() => authDownload(`/documents/${d.id}/download`, { filename: d.filename || d.name })}
                                    data-testid={`document-view-${d.id}`}
                                    className="flex-1 text-center text-xs border border-border hover:border-primary hover:text-primary py-2 transition-colors">
                                    View
                                </button>
                                <button onClick={() => summarize(d)} disabled={aiBusyId === d.id} data-testid={`document-summary-${d.id}`}
                                    className="flex-1 text-xs border border-border hover:border-primary hover:text-primary py-2 transition-colors disabled:opacity-50">
                                    <Sparkles className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />{aiBusyId === d.id ? "Thinking…" : "AI summary"}
                                </button>
                                <button onClick={() => openEsign(d)} data-testid={`document-esign-${d.id}`}
                                    className="flex-1 text-xs border border-border hover:border-primary hover:text-primary py-2 transition-colors">
                                    <Send className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />Send for sign
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="Upload document" size="lg" testId="documents-upload-modal">
                <form onSubmit={onUpload} className="space-y-4">
                    <Field label="Display name" hint="A friendly label. Defaults to the original filename.">
                        <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="e.g. Plaint — Mehra v. State" data-testid="documents-name-input"/>
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Client" hint="Map this document to a client. Optional but recommended.">
                            <Select value={form.client_id} onChange={e => onPickClient(e.target.value)} data-testid="documents-client-select"
                                options={[{ value: "", label: "— no client —" }, ...clients.map(c => ({ value: c.id, label: clientLabel(c) }))]}/>
                        </Field>
                        <Field label="Case (internal # · court #)" hint={form.client_id ? "Only this client's cases shown." : "Pick a case to auto-fill the client."}>
                            <Select value={form.case_id} onChange={e => onPickCase(e.target.value)} data-testid="documents-case-select"
                                options={[{ value: "", label: "— no case —" }, ...filteredCases.map(c => ({ value: c.id, label: caseLabel(c) }))]}/>
                        </Field>
                    </div>
                    <Field label="File">
                        <input ref={fileRef} type="file" required data-testid="documents-file-input"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-muted-foreground file:bg-muted file:border-0 file:px-3 file:py-1.5 file:text-foreground file:text-xs file:mr-3 file:cursor-pointer"/>
                    </Field>
                    <div className="flex justify-end gap-2 pt-2">
                        <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
                        <GoldButton type="submit" disabled={uploading} data-testid="documents-modal-upload-btn">{uploading ? "Uploading…" : "Upload"}</GoldButton>
                    </div>
                </form>
            </Modal>

            <Modal open={!!edit} onClose={() => setEdit(null)} title="Re-map document" size="lg" testId="document-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <Field label="Display name">
                            <Input value={edit.display_name} onChange={e => setEdit({ ...edit, display_name: e.target.value })} data-testid="document-edit-name"/>
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Client">
                                <Select value={edit.client_id} onChange={e => onEditPickClient(e.target.value)} data-testid="document-edit-client"
                                    options={[{ value: "", label: "— no client —" }, ...clients.map(c => ({ value: c.id, label: clientLabel(c) }))]}/>
                            </Field>
                            <Field label="Case (internal # · court #)">
                                <Select value={edit.case_id} onChange={e => onEditPickCase(e.target.value)} data-testid="document-edit-case"
                                    options={[{ value: "", label: "— no case —" }, ...editFilteredCases.map(c => ({ value: c.id, label: caseLabel(c) }))]}/>
                            </Field>
                        </div>
                        <Field label="Remarks (mandatory · logged to audit trail)">
                            <Textarea rows="2" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} data-testid="document-edit-reason" placeholder="e.g. Re-tagged to the correct case after intake review"/>
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <GhostButton type="button" onClick={() => setEdit(null)}>Cancel</GhostButton>
                            <GoldButton type="submit" disabled={editSaving} data-testid="document-edit-save-btn">{editSaving ? "Saving…" : "Save changes"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal open={!!esign} onClose={() => setEsign(null)} title="Send for e-signature" size="xl" testId="document-esign-modal">
                {esign && (
                    <form onSubmit={sendEsign} className="space-y-4">
                        <div className="bg-background border border-border px-3 py-2 text-xs text-muted-foreground">
                            Document: <span className="text-foreground">{esign.document_name}</span>
                        </div>
                        <Field label="Provider">
                            <Select value={esign.provider} onChange={e => setEsign({ ...esign, provider: e.target.value })} data-testid="esign-provider-select"
                                options={[
                                    { value: "docusign", label: "DocuSign — JWT (recommended)" },
                                    { value: "digilocker", label: "DigiLocker — Aadhaar e-sign (plug-ready)" },
                                ]}/>
                        </Field>
                        <Field label="Email subject">
                            <Input value={esign.subject} onChange={e => setEsign({ ...esign, subject: e.target.value })} data-testid="esign-subject-input"/>
                        </Field>
                        <Field label="Email message (optional)">
                            <Textarea rows="2" value={esign.blurb} onChange={e => setEsign({ ...esign, blurb: e.target.value })} data-testid="esign-blurb-input"
                                placeholder="A brief note that appears in the signature request email."/>
                        </Field>
                        <div>
                            <div className="overline mb-2">Recipients</div>
                            <div className="space-y-2">
                                {esign.recipients.map((r, i) => (
                                    <div key={`recipient-${i}-${r.email || ""}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`esign-recipient-${i}`}>
                                        <input value={r.name} onChange={e => updRecipient(i, "name", e.target.value)}
                                            placeholder="Name" data-testid={`esign-recipient-name-${i}`}
                                            className="col-span-4 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm"/>
                                        <input value={r.email} type="email" onChange={e => updRecipient(i, "email", e.target.value)}
                                            placeholder="Email" data-testid={`esign-recipient-email-${i}`}
                                            className="col-span-4 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm"/>
                                        <input value={r.role} onChange={e => updRecipient(i, "role", e.target.value)}
                                            placeholder="Role" data-testid={`esign-recipient-role-${i}`}
                                            className="col-span-2 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm"/>
                                        <input value={r.routing_order} type="number" min="1" onChange={e => updRecipient(i, "routing_order", e.target.value)}
                                            placeholder="Order" data-testid={`esign-recipient-order-${i}`}
                                            className="col-span-1 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm"/>
                                        <button type="button" onClick={() => rmRecipient(i)} disabled={esign.recipients.length === 1}
                                            data-testid={`esign-recipient-remove-${i}`}
                                            className="col-span-1 text-muted-foreground hover:text-red-400 text-xs disabled:opacity-40 disabled:cursor-not-allowed">
                                            <Trash2 className="w-4 h-4 mx-auto" strokeWidth={1.5}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addRecipient} data-testid="esign-add-recipient-btn"
                                className="mt-3 text-xs text-primary hover:underline">+ Add another signer</button>
                            <p className="text-[10px] text-muted-foreground mt-2">
                                Routing order: signers with the same number sign in parallel; higher numbers sign after lower ones complete.
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <GhostButton type="button" onClick={() => setEsign(null)}>Cancel</GhostButton>
                            <GoldButton type="submit" disabled={esignSending} data-testid="esign-send-btn">{esignSending ? "Sending…" : "Send for signature"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>
        </>
    );
}